import type { CanvasNode, Point } from "./domain/types";

/** Original points stay unchanged, so repeated resizing does not lose ink detail. */
export function drawingPoints(node: CanvasNode): Point[] {
  const raw = node.props.points;
  if (!Array.isArray(raw)) return [];
  const points: Point[] = raw.flatMap((point) => {
    if (!point || typeof point !== "object" || Array.isArray(point) || typeof point.x !== "number" || typeof point.y !== "number" || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return [];
    return [{ x: point.x, y: point.y, ...(typeof point.pressure === "number" && Number.isFinite(point.pressure) ? { pressure: Math.max(0, Math.min(1, point.pressure)) } : {}) }];
  });
  const extent = points.reduce((size, point) => ({ x: Math.max(size.x, point.x), y: Math.max(size.y, point.y) }), { x: 1, y: 1 });
  return points.map((point) => ({ ...point, x: node.geometry.x + point.x * node.geometry.width / extent.x, y: node.geometry.y + point.y * node.geometry.height / extent.y }));
}

/** Shared by live preview, saved ink and exports, including taps and pen pressure. */
export function inkSegments(points: Point[], width: number): { d: string; width: number }[] {
  if (!points.length) return [];
  let previous = points[0]!;
  return points.map((point, index) => {
    const next = points[index + 1];
    const end = next ? { x: (point.x + next.x) / 2, y: (point.y + next.y) / 2 } : point;
    const segment = {
      d: `M ${previous.x} ${previous.y} Q ${point.x} ${point.y} ${end.x} ${end.y}`,
      width: width * (0.45 + (point.pressure ?? 0.5) * 1.1),
    };
    previous = end;
    return segment;
  });
}

function distanceToSegment(point: Point, a: Point, b: Point) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / (dx * dx + dy * dy || 1)));
  return Math.hypot(point.x - a.x - t * dx, point.y - a.y - t * dy);
}

export function inkTouchesSweep(node: CanvasNode, from: Point, to: Point, radius: number) {
  const angle = node.geometry.rotation * Math.PI / 180;
  const cx = node.geometry.x + node.geometry.width / 2, cy = node.geometry.y + node.geometry.height / 2;
  const points = drawingPoints(node).map((p) => ({ x: cx + (p.x - cx) * Math.cos(angle) - (p.y - cy) * Math.sin(angle), y: cy + (p.x - cx) * Math.sin(angle) + (p.y - cy) * Math.cos(angle) }));
  const tolerance = radius + (typeof node.props.strokeWidth === "number" ? node.props.strokeWidth : 3);
  const cross = (a: Point, b: Point, c: Point) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  return points.some((b, i) => {
    const a = points[Math.max(0, i - 1)]!;
    if (Math.min(distanceToSegment(a, from, to), distanceToSegment(b, from, to), distanceToSegment(from, a, b), distanceToSegment(to, a, b)) <= tolerance) return true;
    return cross(a, b, from) * cross(a, b, to) < 0 && cross(from, to, a) * cross(from, to, b) < 0;
  });
}
